'use client';

import Icon from '@ant-design/icons';
import { App, Button, Divider, Input, Modal, Popover, Switch, message } from 'antd';
import classNames from 'classnames';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import MoreSvg from '@/components/Icon/svgs/more.svg';
import ShareSvg from '@/components/Icon/svgs/share_new.svg';
import useMatch from '@/hooks/useMatch';
import { useTranslation } from '@/i18n/client';
import { getSessionId } from '@/service/create';
import { blockFriend, deleteFriend, unblockFriend } from '@/service/friend';
import { HOMEPAGE_FRIEND_STATUS, setAutoReply } from '@/service/homePage';
import { createReport, updateRemark } from '@/service/settingModal';
import useFriendStore from '@/store/useFriendStore';
import { useHomepageContext } from '@/ui/HomePage/HomepageContext';
import { EVENT } from '@/utils/event';
import { ROUTER_PATH } from '@/utils/router';
import { copyToClipboard } from '@/utils/utils';

import BlockModal from '../blockModal';
import styles from './index.less';

// Defined outside component to avoid re-creation on every render
const stopPropagationModalRender = (node: ReactNode) => (
  <div onClick={(e) => e.stopPropagation()}>{node}</div>
);

export interface ISettingModalData {
  userId?: string;
  username?: string;
  friendStatus?: string; // 'FRIEND' 表示好友，其它表示非好友/陌生人
  blockedByMe?: boolean;
  remark?: string;
  relationRemark?: string;
  route?: string;
  avatar?: string;
  sessionId?: string;
  myAutoReply?: boolean;
}

interface IProps extends ISettingModalData {
  open: boolean;
  handleClose: () => void;
}

function SettingModal(props: IProps): JSX.Element {
  const {
    open,
    handleClose,
    userId,
    username,
    friendStatus,
    blockedByMe,
    remark,
    relationRemark,
    route,
    avatar,
    sessionId,
    myAutoReply
  } = props;

  const handleCancel = useCallback(
    (event?: ReactMouseEvent<HTMLElement>) => {
      event?.stopPropagation();
      handleClose();
    },
    [handleClose]
  );

  const { t } = useTranslation();
  const deleteFriendById = useFriendStore((state) => state.deleteFriendById);
  const updateFriendItemById = useFriendStore((state) => state.updateFriendItemById);

  const app = App.useApp();
  const modal = app.modal;

  const [nick, setNick] = useState<string>('');
  const [relationShip, setRelation] = useState<string>('');
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  // const [showReportModal, setReport] = useState<boolean>(false);
  const [isBlockedByMe, setIsBlockedByMe] = useState<boolean>(blockedByMe || false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [autoReply, setAutoReplyStatus] = useState<boolean>(!myAutoReply || false);

  const inHomepage = !!useMatch(ROUTER_PATH.homepage);

  const { setHomepageData } = useHomepageContext();

  useEffect(() => {
    setAutoReplyStatus(!myAutoReply || false);
  }, [myAutoReply]);

  useEffect(() => {
    setIsBlockedByMe(blockedByMe || false);
  }, [blockedByMe]);

  const isFriend = friendStatus === 'FRIEND';

  const handleDelete = useCallback(() => {
    if (deleteLoading) return;

    modal.confirm({
      title: t('common.sureToDelete'),
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      onOk: () => {
        return new Promise<void>((resolve, reject) => {
          if (!userId) {
            message.error(t('error.oops'));
            reject();

            return;
          }

          setDeleteLoading(true);
          deleteFriend({ friendId: userId })
            .then(async (res) => {
              if (res.data.code !== 0) {
                throw new Error(t('error.oops'));
              }

              deleteFriendById(userId);
              message.success(t('common.deleteSuccess'));
              handleClose();

              if (inHomepage) {
                setHomepageData((pre) => {
                  return {
                    ...pre,
                    friendStatus: HOMEPAGE_FRIEND_STATUS.STRANGER
                  };
                });
              } else {
                try {
                  const currentSessionId = await getSessionId();
                  const friendSessionId = sessionId;

                  if (friendSessionId && currentSessionId === friendSessionId) {
                    dispatchEvent(new Event(EVENT.CLICK_NEW_CHAT_BUTTON));
                  }
                } catch {
                  message.error(t('error.oops'));
                }
              }

              resolve();
            })
            .catch((e) => {
              message.error(e.message || t('common.deleteFail'));
            })
            .finally(() => {
              setDeleteLoading(false);
            });
        });
      }
    });
    setPopoverOpen(false);
  }, [deleteFriendById, deleteLoading, handleClose, modal, sessionId, t, userId]);

  const handleSave = () => {
    if (!userId) {
      message.error(t('error.oops'));

      return;
    }

    setSaveLoading(true);
    updateRemark({
      friendId: userId,
      remark: nick,
      relationRemark: relationShip
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(t('error.oops'));
        }

        handleClose();
        updateFriendItemById(userId, { remarks: nick, relationRemarks: relationShip });
        message.success(t('homepage.settingModal.saveSuccess'));
      })
      .catch((e) => {
        message.error(e.message || t('error.oops'));
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const handleReport = useCallback(() => {
    if (!userId) {
      message.error(t('error.oops'));

      return;
    }

    createReport({
      reportedUserId: userId
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(t('error.oops'));
        }

        message.success(t('homepage.settingModal.reportSuccess'));
      })
      .catch((e) => {
        message.error(e.message || t('homepage.settingModal.reportFail'));
      });
  }, [t, userId]);

  const handleBlock = () => {
    // 乐观更新
    if (!userId) {
      message.error(t('error.oops'));

      return;
    }

    const prev = isBlockedByMe;

    setIsBlockedByMe((v) => !v);
    (prev ? unblockFriend({ friendId: userId }) : blockFriend({ friendId: userId }))
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        updateFriendItemById(userId, { blockedByMe: !prev });

        prev
          ? message.success(t('homepage.settingModal.unblockSuccess'))
          : message.success(t('homepage.settingModal.blockSuccess'));
      })
      .catch((err) => {
        prev
          ? message.error(err.message || t('homepage.settingModal.FailedToUnBlock'))
          : message.error(err.message || t('homepage.settingModal.FailedToBlock'));
        setIsBlockedByMe(prev);
      });
  };

  const handleSetAutoReply = (val: boolean) => {
    // const prev = autoReply;

    setAutoReplyStatus((v) => !v);

    if (!sessionId) {
      message.error(t('error.oops'));
      setAutoReplyStatus((v) => !v);

      return;
    }

    setAutoReply({ sessionId, autoReply: !val })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(t('error.oops'));
        }

        val
          ? message.success(t('homepage.autoReplyDisabledTips'))
          : message.success(t('homepage.autoReplyEnabledTips'));
      })
      .catch((e) => {
        message.error(e.message || t('error.oops'));
        setAutoReplyStatus((v) => !v);
      });
  };

  useEffect(() => {
    setNick(remark || '');
    setRelation(relationRemark || '');
  }, [remark, relationRemark]);

  const renderPopoverContent = useCallback(() => {
    return (
      <>
        <div className="h-[28px] px-[4px] flex items-flex-start g-[8px] flex-col self-stretch">
          <Button
            className="h-full py-[4px] px-[12px] rounded-[8px] flex items-center"
            onClick={() => {
              setPopoverOpen(false);

              if (!userId) {
                message.error(t('error.oops'));

                return;
              }

              isBlockedByMe ? handleBlock() : setShowBlockModal(true);
            }}
            type="text"
          >
            <span>
              {!isBlockedByMe
                ? t('homepage.settingModal.block')
                : t('homepage.settingModal.unblock')}
            </span>
          </Button>
        </div>
        <Divider className={styles.divider} />
        <div className="h-[28px] px-[4px] flex items-flex-start g-[8px] flex-col self-stretch">
          <Button
            className="h-full py-[4px] px-[12px] rounded-[8px] flex items-center"
            onClick={() => {
              // setReport(true);
              handleReport();
              setPopoverOpen(false);
            }}
            type="text"
          >
            <span>{t('homepage.settingModal.report')}</span>
          </Button>
        </div>
        <Divider className={styles.divider} />
        <div className="h-[28px] px-[4px] flex items-flex-start g-[8px] flex-col self-stretch">
          <Button
            className="h-full py-[4px] px-[12px] rounded-[8px] flex items-center text-[var(--red,#FF4743)]"
            loading={deleteLoading}
            onClick={handleDelete}
            type="text"
          >
            <span className={styles.deleteText}>{t('homepage.settingModal.deleteFriend')}</span>
          </Button>
        </div>
      </>
    );
  }, [t, isBlockedByMe, deleteLoading, userId, handleDelete, handleReport]);

  return (
    <>
      <Modal
        centered={true}
        className={classNames(styles.settingModal, 'p-{16px}')}
        footer={[]}
        modalRender={stopPropagationModalRender}
        onCancel={handleCancel}
        open={open}
        width={580}
        {...(isFriend
          ? {
              cancelText: t('common.cancel'),
              okText: t('common.save')
            }
          : {})}
      >
        <div className={styles.friendContainer}>
          <div className={styles.userInfoSection}>
            <img alt={username} className={styles.avatar} src={avatar} />
            <div>
              <span className={styles.username}>{username}</span>
              <span className={styles.route}>{`${window.location.origin}/${route}`}</span>
            </div>
            {isFriend ? (
              <div className={styles.friendButtonGroup}>
                <Button
                  className={styles.actionButton}
                  onClick={() => {
                    copyToClipboard(`${window.location.origin}/${route}` || '').then(() => {
                      message.success(
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('memoryDetail.shareModal.copySuccessTips', {
                              url: `${window.location.origin}/${route}` || ''
                            })
                          }}
                        />
                      );
                    });
                  }}
                >
                  <Icon className="w-[14px] h-[14px]" component={ShareSvg} />
                </Button>
                <Popover
                  arrow={false}
                  className="rounded-[50%]"
                  content={renderPopoverContent()}
                  onOpenChange={(v) => setPopoverOpen(v)}
                  open={popoverOpen}
                  overlayClassName={styles.settingModalPopover}
                  placement="rightTop"
                  trigger={['hover']}
                >
                  <Button className={styles.actionButton}>
                    <Icon component={MoreSvg} />
                  </Button>
                </Popover>
              </div>
            ) : (
              <div className={styles.strangerButtonGroup}>
                <Button
                  className={classNames(styles.actionButton, 'w-[36px] p-[10px] ')}
                  onClick={() => {
                    copyToClipboard(`${window.location.origin}/${route}` || '').then(() => {
                      message.success(
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t('memoryDetail.shareModal.copySuccessTips', {
                              url: `${window.location.origin}/${route}` || ''
                            })
                          }}
                        />
                      );
                    });
                  }}
                >
                  <Icon component={ShareSvg} />
                </Button>
                <Button
                  className={classNames(styles.actionButton, 'px-[16px] py-[6px] ')}
                  onClick={() => {
                    isBlockedByMe ? handleBlock() : setShowBlockModal(true);
                  }}
                >
                  {!isBlockedByMe
                    ? t('homepage.settingModal.block')
                    : t('homepage.settingModal.unblock')}
                </Button>
                <Button
                  className={classNames(styles.actionButton, 'px-[16px] py-[6px] ')}
                  onClick={() => {
                    // setReport(true)
                    handleReport();
                  }}
                >
                  {t('homepage.settingModal.report')}
                </Button>
              </div>
            )}
          </div>
          {isFriend ? (
            <>
              <div className={styles.inputSection}>
                <div className={styles.sectionTitle}>{t('homepage.settingModal.nickname')}</div>
                <Input
                  className={styles.friendInput}
                  onChange={(e) => setNick(e.target.value)}
                  placeholder={t('homepage.settingModal.nickname')}
                  value={nick}
                />
              </div>
              <div className={styles.inputSection}>
                <div className={styles.sectionTitle}>{t('homepage.settingModal.relationship')}</div>
                <div className={styles.relationshipContainer}>
                  <Input
                    className={styles.friendInput}
                    onChange={(e) => setRelation(e.target.value)}
                    placeholder={t('homepage.settingModal.relationshipDescription')}
                    value={relationShip}
                  />
                  <div className={styles.relationshipTags}>
                    <Button
                      className={styles.relationshipTag}
                      onClick={() =>
                        setRelation((prev) => prev + t('homepage.settingModal.friend'))
                      }
                    >
                      {t('homepage.settingModal.friend')}
                    </Button>
                    <Button
                      className={styles.relationshipTag}
                      onClick={() =>
                        setRelation((prev) => prev + t('homepage.settingModal.family'))
                      }
                    >
                      {t('homepage.settingModal.family')}
                    </Button>
                    <Button
                      className={styles.relationshipTag}
                      onClick={() =>
                        setRelation((prev) => prev + t('homepage.settingModal.coworker'))
                      }
                    >
                      {t('homepage.settingModal.coworker')}
                    </Button>
                  </div>
                </div>
              </div>

              {myAutoReply === undefined ? null : (
                <div className={styles.isAutoReply}>
                  <div className={styles.stopAutoReply}>
                    <span className={styles.replyText}>
                      {t('homepage.settingModal.stopAutoReply')}
                    </span>
                    <Switch
                      checked={autoReply}
                      className="w-[44px] p-[2px] g-[2px] align-center"
                      onChange={(e) => handleSetAutoReply(e)}
                    />
                  </div>
                  <div className={styles.autoReplyDescription}>
                    {t('homepage.settingModal.stopAutoReplyDescription')}
                  </div>
                </div>
              )}

              <div className={styles.friendFooter}>
                <Button className={styles.friendConceal} onClick={handleClose}>
                  <span>{t('common.cancel')}</span>
                </Button>
                <Button className={styles.friendConfirm} loading={saveLoading} onClick={handleSave}>
                  <span>{t('common.save')}</span>
                </Button>
              </div>
            </>
          ) : myAutoReply === undefined ? null : (
            <div className={styles.isAutoReply}>
              <div className={styles.stopAutoReply}>
                <span className={styles.replyText}>{t('homepage.settingModal.stopAutoReply')}</span>
                <Switch
                  checked={autoReply}
                  className="w-[44px] p-[2px] g-[2px] align-center"
                  onChange={(e) => handleSetAutoReply(e)}
                />
              </div>
              <div className={styles.autoReplyDescription}>
                {t('homepage.settingModal.stopAutoReplyDescription')}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <BlockModal onBlock={handleBlock} open={showBlockModal} setOpen={setShowBlockModal} />
      {/* <ReportModal onReport={handleReport} open={showReportModal} setOpen={setReport} /> */}
    </>
  );
}

export default SettingModal;
